import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import SupportModal from 'components/modals/supportModal'
import { RootState } from 'store/redux'
import { toggleSupportModal } from 'store/redux/sidebar'
import useGetProfile from 'modules/auth/profile/hooks/useGetProfile'
import { getBankCards } from 'services/bankManagements'
import { CASHBOX_LINKED_TRUST_STATE, updateCashbox } from 'services/cashbox'
import { PLAN_FREE } from 'pages/fund/buySubscription/utils'
import {
    Card,
    Notice,
    PrimaryButton,
    SecondaryButton,
    apiMessage,
    inputClass,
} from 'modules/cashbox/ui'
import { CASHBOX_KEY, useCashboxTransactions } from 'modules/cashbox/hooks'
import {
    isLinkedCashbox,
    linkedAccountLabel,
} from 'utilities/helper/cashBoxes'

/** Why turning the link on was refused, in Android's order of checks. */
type TBlock = 'plan' | 'profile' | 'has_payment'

const BLOCK_MESSAGE: Record<TBlock, string> = {
    plan: 'برای اتصال حساب مدیر به صندوق، صندوق خود را ارتقاء دهید.',
    profile:
        'برای استفاده از این سرویس نیاز است اطلاعات کاربری خود را تکمیل کنید.',
    has_payment: 'پس از اولین پرداخت، امکان اتصال حساب به صندوق وجود ندارد.',
}

const SETTLEMENT_NOTE =
    'مبالغ واریزی به صندوق تا قبل از ساعت ۲۱:۳۰ روزهای کاری، فردای آن روز بین ساعات ۱۶ الی ۱۸ به حساب متصل به صندوق واریز می‌شود.'

const last4 = (value?: string | number | null) => {
    const digits = String(value ?? '').replace(/\D/g, '')
    return digits.length >= 4 ? `…${digits.slice(-4)}` : ''
}

/**
 * «اتصال حساب» — link a صندوق قرعه‌کشی to one of the manager's own bank
 * accounts, so its cash-ins are settled to that account instead of staying
 * «نزد همیان» (hamyan-pwa#141). Parity with Android's
 * `CashboxManagementActivity`, including its refusals and their wording.
 *
 * One way only: unlinking is a support job on Android too, because the
 * settlement pipeline may already hold money for the account.
 *
 * The save sends the box's SAVED settings, not the form above it: the
 * endpoint needs the full body (`period` included), and linking must not
 * quietly save half-edited fields the manager has not confirmed.
 */
export default function CashboxAccountLink({
    cashbox,
}: {
    cashbox: ICashboxDetail
}) {
    const id = String(cashbox.id)
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const queryClient = useQueryClient()
    const { isOpenSupportModal } = useSelector(
        (state: RootState) => state.sideBarControler
    )
    const linked = isLinkedCashbox(cashbox)

    const [open, setOpen] = useState(false)
    const [blocked, setBlocked] = useState<TBlock | null>(null)
    const [accountId, setAccountId] = useState<number | ''>('')

    const { profileData, isLoading: profileLoading } = useGetProfile()
    // Android's rule is "the box's transaction list is not empty". The
    // endpoint only lists the running period of an ACTIVE box, so a box that
    // has not started (or finished its period) can always be linked — which
    // is also when there is nothing in flight to settle.
    const { data: transactions, isLoading: transactionsLoading } =
        useCashboxTransactions(linked ? undefined : id)
    const { data: cards, isLoading: cardsLoading } = useQuery(
        ['bank-cards'],
        () => getBankCards(),
        { enabled: open }
    )
    const accounts = cards?.results ?? []

    const blockReason = (): TBlock | null => {
        // Service package 2 is the free one, the only package without
        // `FEATURE_PAYMENT` (Android: `hasPermissionCreateWorriedBox`). The
        // box payload carries only the package id, not its features.
        if (cashbox.service_package === PLAN_FREE) return 'plan'
        if (!profileData?.first_name?.trim() || !profileData?.last_name?.trim())
            return 'profile'
        if ((transactions?.results?.length ?? 0) > 0) return 'has_payment'
        return null
    }

    const link = useMutation({
        mutationFn: () =>
            updateCashbox({
                id,
                name: cashbox.name,
                image_id: cashbox.image_id ?? 1,
                notification_announce: cashbox.notification_announce ?? 'jo',
                sms_announce: cashbox.sms_announce ?? 'jo',
                manual_cashout: cashbox.manual_cashout ?? false,
                editable_members: cashbox.editable_members ?? false,
                trust_state: CASHBOX_LINKED_TRUST_STATE,
                bank_account: Number(accountId),
                period: {
                    share_value: cashbox.period?.share_value ?? 0,
                    interval: cashbox.period?.interval ?? 'm',
                    day_of_draw: cashbox.period?.day_of_draw ?? 1,
                    number_of_loans: cashbox.period?.number_of_loans ?? 1,
                    is_manager_first_winner:
                        cashbox.period?.is_manager_first_winner ?? false,
                },
            }),
        onSuccess: () => {
            posthog.capture('cashbox_account_link_saved', {
                cashbox_id: id,
                state: cashbox.state,
            })
            toast.success('صندوق به حساب شما متصل شد')
            setOpen(false)
            queryClient.invalidateQueries(CASHBOX_KEY(id))
            queryClient.invalidateQueries(['get-boxes'])
        },
        onError: (error: any) => {
            posthog.capture('cashbox_account_link_failed', {
                cashbox_id: id,
                status: error?.response?.status,
                code: error?.response?.data?.code,
            })
            toast.error(apiMessage(error))
        },
    })

    const block = (reason: TBlock | 'unlink_support') => {
        posthog.capture('cashbox_account_link_blocked', {
            cashbox_id: id,
            reason,
        })
    }

    const toggle = () => {
        if (open) {
            setOpen(false)
            return
        }
        const reason = blockReason()
        setBlocked(reason)
        if (reason) {
            block(reason)
            return
        }
        posthog.capture('cashbox_account_link_started', { cashbox_id: id })
        setOpen(true)
    }

    const submit = () => {
        if (!accountId) {
            toast.error('انتخاب حساب مدیر الزامیست.')
            return
        }
        if (window.confirm(SETTLEMENT_NOTE)) link.mutate()
    }

    const checking = !linked && (profileLoading || transactionsLoading)

    return (
        <>
            <p className="text-sm font-bold text-black mb-2 mt-6">اتصال حساب</p>
            <Card className="mb-4">
                {linked ? (
                    <>
                        <label className="flex items-start gap-2">
                            <input
                                type="checkbox"
                                checked
                                disabled
                                className="w-4 h-4 mt-0.5 accent-green-1"
                            />
                            <span>
                                <span className="block text-sm text-black">
                                    صندوق به حساب مدیر متصل است
                                </span>
                                <span className="block text-xs text-text-secondary mt-0.5">
                                    {linkedAccountLabel(cashbox.bank_account_data) ||
                                        'حساب متصل'}
                                </span>
                            </span>
                        </label>
                        <div className="mt-3">
                            <Notice>
                                برای قطع اتصال صندوق از حساب مدیر، لطفاً با
                                پشتیبانی تماس بگیرید.
                            </Notice>
                        </div>
                        <div className="mt-3">
                            <SecondaryButton
                                onClick={() => {
                                    block('unlink_support')
                                    dispatch(
                                        toggleSupportModal({
                                            isOpenSupportModal: true,
                                        })
                                    )
                                }}
                            >
                                تماس با پشتیبانی
                            </SecondaryButton>
                        </div>
                    </>
                ) : (
                    <>
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={open}
                                disabled={checking}
                                onChange={toggle}
                                className="w-4 h-4 mt-0.5 accent-green-1"
                            />
                            <span>
                                <span className="block text-sm text-black">
                                    اتصال صندوق به حساب مدیر
                                </span>
                                <span className="block text-xs text-text-secondary mt-0.5 leading-5">
                                    {SETTLEMENT_NOTE}
                                </span>
                            </span>
                        </label>

                        {blocked && !open && (
                            <div className="mt-3">
                                <Notice tone="warn">
                                    {BLOCK_MESSAGE[blocked]}
                                </Notice>
                                {blocked === 'plan' && (
                                    <div className="mt-3">
                                        <SecondaryButton
                                            onClick={() =>
                                                navigate(`/cashbox/${id}/plans`)
                                            }
                                        >
                                            ارتقاء صندوق
                                        </SecondaryButton>
                                    </div>
                                )}
                                {blocked === 'profile' && (
                                    <div className="mt-3">
                                        <SecondaryButton
                                            onClick={() =>
                                                navigate('/account/profile')
                                            }
                                        >
                                            تکمیل اطلاعات کاربری
                                        </SecondaryButton>
                                    </div>
                                )}
                            </div>
                        )}

                        {open && (
                            <div className="mt-3">
                                {cardsLoading ? (
                                    <p className="text-xs text-text-secondary">
                                        در حال دریافت حساب‌ها…
                                    </p>
                                ) : accounts.length === 0 ? (
                                    <Notice tone="warn">
                                        هنوز حساب بانکی ثبت نکرده‌اید.
                                    </Notice>
                                ) : (
                                    <select
                                        value={accountId}
                                        onChange={(event) =>
                                            setAccountId(
                                                Number(event.target.value) || ''
                                            )
                                        }
                                        className={inputClass}
                                    >
                                        <option value="">انتخاب حساب…</option>
                                        {accounts.map((account) => (
                                            <option
                                                key={account.id}
                                                value={account.id}
                                            >
                                                {[
                                                    account.bank_name,
                                                    account.full_name,
                                                    last4(
                                                        account.card_number ||
                                                            account.iban
                                                    ),
                                                ]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </option>
                                        ))}
                                    </select>
                                )}
                                <button
                                    type="button"
                                    className="mt-2 text-xs text-green-1 font-bold"
                                    onClick={() =>
                                        navigate('/manage-bank-cards/add')
                                    }
                                >
                                    + افزودن حساب بانکی
                                </button>
                                <div className="mt-3">
                                    <PrimaryButton
                                        disabled={link.isLoading}
                                        onClick={submit}
                                    >
                                        {link.isLoading
                                            ? 'در حال اتصال…'
                                            : 'اتصال حساب'}
                                    </PrimaryButton>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Card>

            <SupportModal
                show={isOpenSupportModal}
                onHide={() =>
                    dispatch(toggleSupportModal({ isOpenSupportModal: false }))
                }
            />
        </>
    )
}
